/**
 * License and Trial Management Service
 *
 * Handles API calls for license information and trial management.
 *
 * @author CAVU Innovations
 * @version 1.0.0
 */

import { API_BASE_URL } from '../config/api-config';

// API Endpoints — absolute URLs to API_BASE_URL so the request goes directly
// to the backend host (qa-pdf-api / pdf-api). Relative URLs target the
// frontend host instead, which is a different cookie domain from where the
// JWT was set during OAuth2 callback — so the jwt cookie wouldn't reach the
// backend and SecurityContext would be empty, bypassing the cross-customer
// activation check.
const API_ENDPOINTS = {
  LICENSE_INFO: `${API_BASE_URL}/api/pdf/license-info`,
  LICENSE_APPLY: `${API_BASE_URL}/api/pdf/license/apply`,
  LICENSE_CLEAR: `${API_BASE_URL}/api/pdf/license/clear`,
  TRIAL_CANCEL: `${API_BASE_URL}/api/pdf/trial/cancel`,
} as const;

// localStorage key for storing the license key hash (customer identity for tenant isolation)
const LICENSE_KEY_HASH_STORAGE_KEY = 'licenseKeyHash';

/**
 * Get the stored license key hash from localStorage.
 */
export function getStoredLicenseKeyHash(): string | null {
  try {
    return localStorage.getItem(LICENSE_KEY_HASH_STORAGE_KEY);
  } catch {
    return null;
  }
}

// localStorage key for the signed-in user's email (used for session attribution).
const USER_EMAIL_STORAGE_KEY = 'userEmail';

/** Get the stored signed-in user's email from localStorage. */
export function getStoredUserEmail(): string | null {
  try {
    return localStorage.getItem(USER_EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Store (or clear, when null) the signed-in user's email in localStorage. */
export function storeUserEmail(email: string | null): void {
  try {
    if (email) localStorage.setItem(USER_EMAIL_STORAGE_KEY, email);
    else localStorage.removeItem(USER_EMAIL_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Store the license key hash in localStorage.
 */
function storeLicenseKeyHash(hash: string): void {
  try {
    localStorage.setItem(LICENSE_KEY_HASH_STORAGE_KEY, hash);
  } catch (e) {
    console.warn('Failed to store license key hash in localStorage:', e);
  }
}

/**
 * Remove the stored license key hash from localStorage.
 */
export function clearStoredLicenseKeyHash(): void {
  try {
    localStorage.removeItem(LICENSE_KEY_HASH_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Build headers that include the license key hash for customer identification.
 * The backend uses X-License-Key-Hash as a fallback when no JWT is present.
 */
function getLicenseHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const hash = getStoredLicenseKeyHash();
  if (hash) {
    headers['X-License-Key-Hash'] = hash;
  }
  return headers;
}

/**
 * Inspect a fetch response for the X-Clear-License header. The backend
 * CustomerIsolationFilter sets this when it refuses a request whose
 * X-License-Key-Hash doesn't match the authenticated user's customer.
 *
 * When detected: clear the stale hash from localStorage and dispatch
 * license-changed so EditorResetBridge wipes session state. User ends up
 * in NO_LICENSE state and must enter their own license.
 *
 * Memory: project_cross_customer_bleed_on_signin (Option (d) Step 2).
 */
function handleClearLicenseHeader(response: Response): void {
  if (response.headers.get('X-Clear-License') === 'true') {
    console.warn('Backend instructed license clear (cross-customer mismatch). Wiping localStorage.');
    clearStoredLicenseKeyHash();
    window.dispatchEvent(new CustomEvent('license-changed'));
  }
}

/**
 * Trial information from the license-info response.
 */
export interface TrialInfo {
  hasTrial: boolean;
  status: 'PENDING' | 'COMPLETED' | 'CANCELED' | 'FAILED';
  selectedTier: string;
  priceFormatted: string;
  amountCents: number;
  currency: string;
  trialStartDate: string;
  trialEndDate: string;
  autoCaptureDate: string;
  daysRemaining: number;
  canCancel: boolean;
  message: string;
}

/**
 * Period information from license-info.
 */
export interface PeriodInfo {
  periodStart: string;
  periodEnd: string;
  daysUntilReset: number;
  resetsOn: string;
}

/**
 * Usage information (PDF uploads, code generation, etc.)
 */
export interface UsageInfo {
  current: number;
  limit: number;
  percentUsed?: number;
  resetsOn?: string;
  allocated?: number;  // Total allocated to generated projects
}

/**
 * Code generation info.
 */
export interface CodeGenInfo {
  enabled: boolean;
  current: number;
  limit: number;
}

/**
 * Complete license information response.
 */
export interface LicenseInfo {
  tier: string;
  periodType: string;
  licenseKey?: string;
  customerName?: string;
  expiresAt?: string;
  currentPeriod: PeriodInfo;
  pdfUploads: UsageInfo;
  codeGeneration: CodeGenInfo;
  flattenedPdfs: UsageInfo;
  tierStatus?: Record<string, unknown>;
  trial?: TrialInfo;
}

/**
 * Response from license application.
 */
export interface ApplyLicenseResponse {
  success: boolean;
  previousTier?: string;
  newTier?: string;
  tierConfig?: {
    tier: string;
    periodType: string;
    maxUniquePdfs: number;
    maxSpringBootProjects: number;
    maxFlattenedPdfs: number;
    codeGeneration: boolean;
  };
  expiresAt?: string;
  licenseKeyHash?: string;
  error?: string;
  errorCode?: string;
  message?: string;
}

/**
 * Response from trial cancellation request.
 */
export interface CancelTrialResponse {
  success: boolean;
  message: string;
}

/**
 * Fetch current license information including trial status.
 */
export async function fetchLicenseInfo(): Promise<LicenseInfo | null> {
  try {
    const response = await fetch(API_ENDPOINTS.LICENSE_INFO, {
      credentials: 'include',
      headers: getLicenseHeaders(),
    });

    handleClearLicenseHeader(response);

    if (!response.ok) {
      console.error('Failed to fetch license info:', response.status);
      return null;
    }

    const data = await response.json();
    return data as LicenseInfo;
  } catch (error) {
    console.error('Error fetching license info:', error);
    return null;
  }
}

/**
 * Apply a new license key.
 */
export async function applyLicenseKey(licenseKey: string): Promise<ApplyLicenseResponse> {
  try {
    const response = await fetch(API_ENDPOINTS.LICENSE_APPLY, {
      method: 'POST',
      credentials: 'include',
      headers: getLicenseHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ licenseKey: licenseKey.trim() }),
    });

    handleClearLicenseHeader(response);

    const data = await response.json();

    // Store the license key hash for subsequent requests (tenant identification)
    if (data.success && data.licenseKeyHash) {
      storeLicenseKeyHash(data.licenseKeyHash);
    }

    // Notify all license-aware components to refresh
    if (data.success) {
      window.dispatchEvent(new CustomEvent('license-changed'));
    }

    return data as ApplyLicenseResponse;
  } catch (error) {
    console.error('Error applying license:', error);
    return {
      success: false,
      error: 'Network error',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Clear/remove current license (returns to the anonymous default).
 */
export async function clearLicense(): Promise<ApplyLicenseResponse> {
  try {
    const response = await fetch(API_ENDPOINTS.LICENSE_CLEAR, {
      method: 'POST',
      credentials: 'include',
      headers: getLicenseHeaders({ 'Content-Type': 'application/json' }),
    });

    handleClearLicenseHeader(response);

    const data = await response.json();

    // Clear stored license key hash on successful license removal
    if (data.success) {
      clearStoredLicenseKeyHash();
      window.dispatchEvent(new CustomEvent('license-changed'));
    }

    return data as ApplyLicenseResponse;
  } catch (error) {
    console.error('Error clearing license:', error);
    return {
      success: false,
      error: 'Network error',
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Request trial cancellation (triggers email verification).
 */
export async function requestTrialCancellation(): Promise<CancelTrialResponse> {
  try {
    const response = await fetch(API_ENDPOINTS.TRIAL_CANCEL, {
      method: 'POST',
      credentials: 'include',
      headers: getLicenseHeaders({ 'Content-Type': 'application/json' }),
    });

    handleClearLicenseHeader(response);

    const data = await response.json();

    // Notify license-aware components to refresh, matching the pattern used by
    // applyLicenseKey and clearLicense.
    if (data.success) {
      window.dispatchEvent(new CustomEvent('license-changed'));
    }

    return data as CancelTrialResponse;
  } catch (error) {
    console.error('Error requesting trial cancellation:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Helper to check if license is in active trial.
 */
export function isTrialActive(licenseInfo: LicenseInfo | null): boolean {
  return licenseInfo?.trial?.hasTrial === true;
}

/**
 * Helper to check if license is a paid tier (not FREE, TRIAL, or NO_LICENSE).
 *
 * NO_LICENSE is the backend's response after a license is cleared and no
 * license is configured. It must NOT be treated as a paid tier — the Header
 * uses this helper to decide between the green "Manage License" button (paid)
 * and the amber "License" entry button (no/free license).
 */
export function isPaidTier(licenseInfo: LicenseInfo | null): boolean {
  if (!licenseInfo) return false;
  const tier = licenseInfo.tier?.toUpperCase();
  return tier !== 'ANONYMOUS' && tier !== 'TRIAL' && tier !== 'NO_LICENSE';
}

/**
 * Get display text for trial countdown (shows end date).
 */
export function getTrialCountdownText(licenseInfo: LicenseInfo | null): string | null {
  if (!licenseInfo?.trial?.hasTrial) return null;

  const trialEndDate = licenseInfo.trial.trialEndDate;
  if (!trialEndDate) return null;

  // Parse date-only string as local date to avoid timezone shift
  const [year, month, day] = trialEndDate.split('T')[0].split('-').map(Number);
  const endDate = new Date(year, month - 1, day);

  return `Ends ${endDate.toLocaleDateString()}`;
}
